import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { AxiosError } from "axios";
import { SaleRoute, SaleRouteRequest } from "../../../types/sale-route.type";
import { NoRunDay } from "../../../types/no-run-day.type";
import {
  createSaleReport,
  exportNoRunDayWebIt,
  getAllRoutesByCompany,
  getNoRunDays,
} from "../../../services";
import { ErrorResponse, handleAxiosError } from "../../../errors/errorHandler";
import { numberWithCommas } from "../../../utils";
import { saveBlobResponse } from "../../../utils/downloadBlob";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateValidationError } from "@mui/x-date-pickers/models";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";

// The create failure a สายไม่วิ่ง day produces: no check-in means no waste
// checker, so the report cannot be built — but the van-load may still need a
// WEB_IT file.
const NO_RUN_CANDIDATE_ERROR = "SalesReportWasteCheckerDataNotFoundError";

const isNoRunCandidate = (error: unknown) => {
  const data = (error as AxiosError<ErrorResponse>)?.response?.data;
  return (
    data?.message === NO_RUN_CANDIDATE_ERROR ||
    data?.error === NO_RUN_CANDIDATE_ERROR
  );
};

/** The สายไม่วิ่ง row for exactly this route + day, or null when there is none. */
const findNoRunDay = async (
  route: string,
  date: string,
): Promise<NoRunDay | null> => {
  try {
    const {
      data: { data },
    } = await getNoRunDays(
      { start_date: date, end_date: date, route, page: 1, limit: 1 },
      { suppressErrorToast: true },
    );
    return (
      data?.rows.find(
        (row) => row.route_code === route && row.stock_date === date,
      ) ?? null
    );
  } catch {
    // The lookup is only a nicer answer to the create failure; if it fails too
    // the create error is still the right thing to show.
    return null;
  }
};

interface ModalCreateReportProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  setTriggerFetch: Dispatch<SetStateAction<number>>;
}

export default function ModalCreateReport({
  open,
  setOpen,
  setTriggerFetch,
}: ModalCreateReportProps) {
  const navigate = useNavigate();

  const [selectedRoute, setSelectedRoute] = useState<string>("");
  const [saleRoutes, setSaleRoutes] = useState<SaleRoute[]>([]);
  const [reportDate, setReportDate] = useState<Dayjs | null>(null);
  const [dateError, setDateError] = useState<DateValidationError>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [noRunDay, setNoRunDay] = useState<NoRunDay | null>(null);
  const [downloading, setDownloading] = useState<boolean>(false);

  const getRoutes = useCallback(async () => {
    const {
      data: { data },
    } = await getAllRoutesByCompany({ includeSpecial: true });

    // Route 99 (ออเดอร์พิเศษ) gets a sales report like any other route on the days it has bills.
    setSaleRoutes(data);
  }, []);

  useEffect(() => {
    getRoutes();
  }, [getRoutes]);

  const handleCreateReport = async () => {
    setLoading(true);
    setNoRunDay(null);
    try {
      // Send report_date only when a valid day is picked; otherwise BE defaults to today.
      const payload: SaleRouteRequest = { route: selectedRoute };
      if (reportDate?.isValid()) {
        payload.report_date = reportDate.format("YYYY-MM-DD");
      }

      const response = await createSaleReport(payload, {
        suppressErrorToast: true,
      });
      setLoading(false);
      handleClose();
      setTriggerFetch?.((prev) => prev + 1);
      navigate(`/report/sale/details/${response.data?.data?.uuid}`);
    } catch (error) {
      // The toast was suppressed so a สายไม่วิ่ง day can answer with its WEB_IT
      // download instead; every other failure is toasted exactly as before.
      const found = isNoRunCandidate(error)
        ? await findNoRunDay(
            selectedRoute,
            (reportDate?.isValid() ? reportDate : dayjs()).format(
              "YYYY-MM-DD",
            ),
          )
        : null;
      if (found) {
        setNoRunDay(found);
      } else if (!(error instanceof Error && error.name === "CanceledError")) {
        handleAxiosError(error as AxiosError<ErrorResponse>);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadNoRun = async () => {
    if (!noRunDay || downloading) return;
    setDownloading(true);
    try {
      const response = await exportNoRunDayWebIt(
        noRunDay.route_code,
        noRunDay.stock_date,
      );
      saveBlobResponse(
        response,
        `web-it-no-run-${noRunDay.route_code}-${noRunDay.stock_date}.xlsx`,
      );
    } catch {
      // Already surfaced by the axios interceptor.
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = () => {
    setReportDate(null);
    setDateError(null);
    setNoRunDay(null);
    setOpen(false);
  };

  const handleConfirm = () => {
    handleCreateReport();
  };

  // A picked date must be a valid, in-range day; an empty field is fine (BE uses today).
  const hasInvalidDate =
    Boolean(dateError) || (reportDate !== null && !reportDate.isValid());
  const dateHelperText = hasInvalidDate
    ? dateError === "disableFuture"
      ? "ไม่สามารถเลือกวันที่ในอนาคต"
      : "รูปแบบวันที่ไม่ถูกต้อง"
    : reportDate
      ? " "
      : "เว้นว่างไว้เพื่อสร้างรายงานของวันนี้";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: {
          sx: {
            padding: 0,
          },
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Typography fontWeight={700}>สร้างรายงานการขายใหม่</Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack gap={2} sx={{ mt: 1 }}>
          <FormControl fullWidth required>
            <InputLabel id="sale-route-label">สายส่ง</InputLabel>
            <Select
              labelId="sale-route-label"
              value={selectedRoute}
              label="สายส่ง"
              onChange={(e) => {
                setSelectedRoute(e.target.value);
                setNoRunDay(null);
              }}
            >
              {saleRoutes.map((route) => (
                <MenuItem key={route.route_code} value={route.route_code}>
                  {route.route_code} - {route.route_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
            <DatePicker
              label="วันที่ของรายงาน (ไม่บังคับ)"
              value={reportDate}
              format="DD/MM/YYYY"
              disableFuture
              onChange={(value) => {
                setReportDate(value);
                setNoRunDay(null);
              }}
              onError={(error) => setDateError(error)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: hasInvalidDate,
                  helperText: dateHelperText,
                },
                field: { clearable: true },
              }}
            />
          </LocalizationProvider>

          {noRunDay && (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  loading={downloading}
                  onClick={handleDownloadNoRun}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  ดาวน์โหลด web-it (สายไม่วิ่ง)
                </Button>
              }
              sx={{ flexWrap: "wrap", "& .MuiAlert-action": { pl: 0 } }}
            >
              สาย {noRunDay.route_code} วันที่{" "}
              {dayjs(noRunDay.stock_date).format("DD/MM/YYYY")} ไม่มีการวิ่ง
              (ไม่มีการเช็คอิน) แต่มีของเข้า{" "}
              {numberWithCommas(noRunDay.total_qty)} ชิ้น (
              {numberWithCommas(noRunDay.product_count)} รายการ)
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleConfirm}
          variant="contained"
          fullWidth
          disabled={!selectedRoute || hasInvalidDate}
          loading={loading}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </Dialog>
  );
}
